import { useDebounce } from '@/hooks/useDebounce';
import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';
import {
  fetchAllBreakdownOptions,
  fetchAudioQuality,
  fetchBreakdown,
  fetchComparisonDetail,
  fetchComparisonPage,
  fetchMetrics,
  fetchRegeneration,
  fetchTimeline,
  fetchVerification,
} from './api';
import type { BreakdownDimension, SummaryQualityFilters, TimelineBucket, VerificationDimension } from './types';

export const SUMMARY_QUALITY_FILTER_DEBOUNCE_MS = 300;
const SUMMARY_QUALITY_STALE_MS = 5 * 60 * 1000;

/**
 * First key segment for every analytics read (metrics/timeline/breakdown/regeneration/
 * verification/filter-options). Kept distinct from the comparisons list and the PHI
 * comparison-detail query, which use their own literal keys below, so the page's
 * "Uppfæra" button can invalidate analytics alone via this prefix.
 */
export const SUMMARY_QUALITY_ANALYTICS_KEY = 'summary-quality-analytics';

export const summaryQualityFilterKey = (filters: SummaryQualityFilters) =>
  [
    SUMMARY_QUALITY_ANALYTICS_KEY,
    filters.from,
    filters.to,
    filters.orgId,
    filters.accountId,
    filters.model,
    filters.promptHash,
    filters.mode,
    filters.templateId,
    filters.includeBackfilled,
  ] as const;

/** Debounced filters, so typing a date doesn't fire (and audit) a read per keystroke. */
export const useDebouncedFilters = (filters: SummaryQualityFilters) =>
  useDebounce(filters, SUMMARY_QUALITY_FILTER_DEBOUNCE_MS);

export const useSummaryMetrics = (filters: SummaryQualityFilters) =>
  useQuery({
    queryKey: [...summaryQualityFilterKey(filters), 'metrics'],
    queryFn: ({ signal }) => fetchMetrics(filters, { signal }),
    staleTime: SUMMARY_QUALITY_STALE_MS,
  });

export const useAudioQualityAnalytics = (
  filters: Pick<SummaryQualityFilters, 'from' | 'to' | 'orgId' | 'mode'>,
  bucket: TimelineBucket,
  scorerVersion: string | null
) =>
  useQuery({
    queryKey: [
      SUMMARY_QUALITY_ANALYTICS_KEY,
      'audio',
      filters.from,
      filters.to,
      filters.orgId,
      filters.mode,
      bucket,
      scorerVersion,
    ],
    queryFn: ({ signal }) => fetchAudioQuality(filters, bucket, scorerVersion, { signal }),
    staleTime: SUMMARY_QUALITY_STALE_MS,
  });

export const useSummaryTimeline = (filters: SummaryQualityFilters, bucket: TimelineBucket) =>
  useQuery({
    queryKey: [...summaryQualityFilterKey(filters), 'timeline', bucket],
    queryFn: ({ signal }) => fetchTimeline(filters, bucket, { signal }),
    staleTime: SUMMARY_QUALITY_STALE_MS,
  });

export const useSummaryBreakdown = (filters: SummaryQualityFilters, by: BreakdownDimension) =>
  useQuery({
    queryKey: [...summaryQualityFilterKey(filters), 'breakdown', by],
    queryFn: ({ signal }) => fetchBreakdown(filters, by, { signal }),
    staleTime: SUMMARY_QUALITY_STALE_MS,
    // Keeps the previous rows only when `by` (the grouping dimension) is unchanged, so
    // a date-range change shows stale rows briefly while a grouping change shows the
    // loading state instead of the old dimension's rows rendered under the new label.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey.at(-1) === by ? previousData : undefined,
  });

export const useSummaryRegeneration = (filters: SummaryQualityFilters) =>
  useQuery({
    queryKey: [...summaryQualityFilterKey(filters), 'regeneration'],
    queryFn: ({ signal }) => fetchRegeneration(filters, { signal }),
    staleTime: SUMMARY_QUALITY_STALE_MS,
  });

export const useSummaryVerification = (filters: SummaryQualityFilters, by: VerificationDimension) =>
  useQuery({
    queryKey: [...summaryQualityFilterKey(filters), 'verification', by],
    queryFn: ({ signal }) => fetchVerification(filters, by, { signal }),
    staleTime: SUMMARY_QUALITY_STALE_MS,
    // See useSummaryBreakdown: don't show a different dimension's rows as a placeholder.
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey.at(-1) === by ? previousData : undefined,
  });

/**
 * Dropdown options come from a breakdown over the date range alone, so picking one
 * filter doesn't empty the other dropdowns. Pages through every group (see
 * `fetchAllBreakdownOptions`), so this gets its OWN key ('breakdown-options', not
 * 'breakdown') rather than sharing the tab's single-page breakdown query: sharing
 * would make the on-screen breakdown table render up to 1000 rows instead of one page
 * whenever the dashboard filters happen to be unfiltered.
 */
export const useFilterOptionRows = (
  range: Pick<SummaryQualityFilters, 'from' | 'to' | 'includeBackfilled'>,
  by: BreakdownDimension,
  enabled = true
) => {
  const optionFilters: SummaryQualityFilters = {
    ...range,
    orgId: null,
    accountId: null,
    model: null,
    promptHash: null,
    mode: null,
    templateId: null,
  };
  return useQuery({
    queryKey: [...summaryQualityFilterKey(optionFilters), 'breakdown-options', by],
    queryFn: ({ signal }) => fetchAllBreakdownOptions(optionFilters, by, { signal }),
    staleTime: SUMMARY_QUALITY_STALE_MS,
    enabled,
  });
};

export const useComparisonPage = (page: number, dateRange: Pick<SummaryQualityFilters, 'from' | 'to'>) =>
  useQuery({
    queryKey: ['summary-quality', 'comparisons', page, dateRange.from, dateRange.to],
    queryFn: ({ signal }) => fetchComparisonPage(page, dateRange, { signal }),
    placeholderData: keepPreviousData,
  });

/**
 * Summary text is PHI: never cache it past the dialog, and refetch on every open so
 * each view reaches the audited BFF route.
 */
export const useComparisonDetail = (audioId: number | null) =>
  useQuery({
    queryKey: ['summary-quality', 'comparison-detail', audioId],
    queryFn: audioId == null ? skipToken : ({ signal }) => fetchComparisonDetail(audioId, { signal }),
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });
